import React, { useRef, useState, useEffect } from 'react';
import { Button, Typography, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface UserProfilePictureUploaderProps {
    onPictureChange: (file: File | null) => void;
    imageUrl: string | null;
    isPictureUploaded: boolean;
}

const UserProfilePictureUploader: React.FC<UserProfilePictureUploaderProps> = ({ onPictureChange, imageUrl, isPictureUploaded }) => {
    const fallbackImageUrl = '/noPhoto.png';
    const [profilePicture, setProfilePicture] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(imageUrl || fallbackImageUrl);
    const [initialImageUrl, setInitialImageUrl] = useState<string | null>(imageUrl);
    const [showRemoveButton, setShowRemoveButton] = useState(false);
    const [isNewPictureSelected, setIsNewPictureSelected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        setPreviewUrl(imageUrl || fallbackImageUrl);
        setInitialImageUrl(imageUrl);
        setProfilePicture(null);
        setShowRemoveButton(false);
        setIsNewPictureSelected(false);
        setError(null);
    }, [imageUrl]);

    useEffect(() => {
        if (isPictureUploaded) {
            setShowRemoveButton(false);
        }
    }, [isPictureUploaded]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];

            if (selectedFile.type !== 'image/png') {
                setError(t("userProfile.errorInvalidImageType"));
                return;
            }

            setProfilePicture(selectedFile);
            onPictureChange(selectedFile);

            const url = URL.createObjectURL(selectedFile);
            setPreviewUrl(url);
            setShowRemoveButton(true);
            setIsNewPictureSelected(true);
            setError(null);
        }
    };

    const handleClick = () => {
        if (inputRef.current) inputRef.current.click();
    };

    const handleRemove = () => {
        setProfilePicture(null);
        setPreviewUrl(initialImageUrl || fallbackImageUrl);
        onPictureChange(null);
        setShowRemoveButton(false);
        setIsNewPictureSelected(false);
        setError(null);

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleCloseSnackbar = () => {
        setError(null);
    };

    return (
        <div>
            <Typography variant="h5" gutterBottom sx={{ mb: 10 }}>
                {t("userProfile.updateProfilePicture")}
            </Typography>
            <input
                type="file"
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Button variant="contained" color="primary" onClick={handleClick}>
                    {t("userProfile.selectPicture")}
                </Button>
                {showRemoveButton && (
                    <Button variant="outlined" color="secondary" onClick={handleRemove}>
                        {t("userProfile.removePicture")}
                    </Button>
                )}
            </div>
            {previewUrl && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Typography variant="body1">
                        {isNewPictureSelected ? t("userProfile.updateCurrentPicture") : (previewUrl === fallbackImageUrl ? t("userProfile.noPhotoUploaded") : t("userProfile.currentPicture"))}
                    </Typography>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                        <img
                            src={previewUrl}
                            alt="Profile Preview"
                            style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '75px' }}
                        />
                    </div>
                </div>
            )}
            {profilePicture && !previewUrl && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                    <Typography variant="body1">
                        {t("userProfile.selectedFile")}: {profilePicture.name}
                    </Typography>
                </div>
            )}
            <Snackbar
                open={!!error}
                message={error}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
            />
        </div>
    );
};

export default UserProfilePictureUploader;
